import { useMemo } from "react"
import type { FC, ReactElement } from "react"

import { AnimatePresence, m, useReducedMotion } from "framer-motion"
import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import type { MotionNodeAnimationOptions } from "motion-dom"

import type { TEmptyRenderCallback } from "@/shared/types/functions"

import { useCoachingChatContext as useChatContext } from "../context"
import {
    useCoachingChatConversationErrorSelector as useConversationErrorSelector,
    useCoachingChatConversationMessagesPresenceSelector as useConversationMessagesPresenceSelector,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector,
    useCoachingChatFormSelector as useFormSelector
} from "../store"
import { coachingChatStyles as chatStyles } from "../styles"
import {
    ECoachingChatConversationErrorVariant as EConversationErrorVariant,
    ECoachingChatConversationStatus as EConversationStatus,
    type TCoachingChatConversationError as TConversationError
} from "../types"

type TProps = {
    renderTypingIndicator(isVisible: boolean): ReactElement
    renderMessages: TEmptyRenderCallback
    renderErrorBox: TEmptyRenderCallback
    renderSummary: TEmptyRenderCallback
}

const {
    conversation: { feed: classes }
} = chatStyles

const Feed: FC<TProps> = ({
    renderTypingIndicator,
    renderMessages,
    renderErrorBox,
    renderSummary
}: TProps): ReactElement => {
    const isMotionReduced: boolean = useReducedMotion()

    const motionProps: MotionNodeAnimationOptions = useMemo(
        (): MotionNodeAnimationOptions =>
            isMotionReduced
                ? {
                      initial: { opacity: 0 },
                      animate: { opacity: 1, transition: { duration: 0.12 } },
                      exit: { opacity: 0, transition: { duration: 0.1 } }
                  }
                : {
                      initial: { opacity: 0, y: 6 },
                      animate: { opacity: 1, y: 0, transition: { duration: 0.18, ease: [0.16, 1, 0.3, 1] } },
                      exit: { opacity: 0, y: 6, transition: { duration: 0.12, ease: [0.4, 0, 1, 1] } }
                  },
        [isMotionReduced]
    )

    const {
        entityId,
        conversation: { feedRef }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const { variant: errorVariant }: TConversationError = useConversationErrorSelector(entityId)

    const { isFilling: areSeedsFeeling }: ReturnType<typeof useFormSelector> = useFormSelector(entityId)

    const areMessagesPresent: boolean = useConversationMessagesPresenceSelector(entityId)

    const status: EConversationStatus = useConversationStatusSelector(entityId)

    const isVisible: boolean = includes<EConversationStatus>(
        [
            EConversationStatus.Ready,
            EConversationStatus.ReadyToStart,
            EConversationStatus.Created,
            EConversationStatus.Connected,
            EConversationStatus.Paused,
            EConversationStatus.InProgress,
            EConversationStatus.GeneratingSummary,
            EConversationStatus.Completed
        ],
        status
    )

    const isAiStuck: boolean = eq(errorVariant, EConversationErrorVariant.AiStuck)

    return (
        <AnimatePresence initial={false} mode="wait" presenceAffectsLayout>
            {isVisible && (
                <m.div {...motionProps} layout ref={feedRef} className={classes.container({ isAiStuck })}>
                    <div className={classes.feed({ isAiStuck, areMessagesPresent })}>
                        {renderMessages()}

                        {renderTypingIndicator(eq(status, EConversationStatus.ReadyToStart) && !areSeedsFeeling)}

                        {renderErrorBox()}

                        {renderSummary()}
                    </div>
                </m.div>
            )}
        </AnimatePresence>
    )
}

Feed.displayName = "CoachingChatFeed"

export { Feed as CoachingChatFeed }
